// src/components/GenerateReportTab.js
import React, { useState, useEffect } from 'react';
import {
    Box,
    Typography,
    Button,
    Alert,
    Card,
    CardContent,
    Grid,
    Divider,
    Chip,
    List,
    ListItem,
    ListItemIcon,
    ListItemText,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    Checkbox,
    TextField
} from '@mui/material';
import CheckCircleIcon from '@mui/icons-material/CheckCircle';
import ErrorIcon from '@mui/icons-material/Error';
import WarningIcon from '@mui/icons-material/Warning';
import DownloadIcon from '@mui/icons-material/Download';
import { fetchRadiologicalImpressions } from '../api';
import { useFormContext, useFormSubmission, useFormValidation } from '../context/FormContext';
import SinusView from './SinusView';
import SagittalViewTab from './SagittalViewTab';

const GenerateReportTab = () => {
    const { state, actions, computed } = useFormContext();
    const { submitForm } = useFormSubmission();
    const { validateForm } = useFormValidation();
    const [success, setSuccess] = useState(false);
    const [validationDialog, setValidationDialog] = useState(false);
    const [validationErrors, setValidationErrors] = useState([]);
    const [impressions, setImpressions] = useState({ mandible: [], maxilla: [] });

    useEffect(() => {
        const loadImpressions = async () => {
            try {
                const response = await fetchRadiologicalImpressions();
                setImpressions(response.data);
            } catch (error) {
                console.error("Failed to fetch radiological impressions:", error);
            }
        };
        loadImpressions();
    }, []);

    const handleImpressionsChange = (event) => {
        const { target: { value } } = event;
        actions.setRadiologicalImpressions(value);
    };

    const handleOtherImpressionChange = (event) => {
        actions.setOtherImpression(event.target.value);
    };

    console.log('GenerateReportTab Rendered. Current State:', {
        patientData: state.patientData ? 'Available' : 'Not available',
        reportType: state.reportType,
        canalsTraced: state.canalsTraced ? 'Available' : 'Not available',
        panoramicData: {
            file1: state.panoramicViewData?.panoramicFile1 ? 'Available' : 'Not available',
            file2: state.panoramicViewData?.panoramicFile2 ? 'Available' : 'Not available',
            comments: state.panoramicViewData?.comments ? 'Available' : 'Not available'
        },
        dimensionsData: {
            image1: state.dimensionsData?.croppedImage1 ? 'Available' : 'Not available',
            image2: state.dimensionsData?.croppedImage2 ? 'Available' : 'Not available'
        },
        regionDetails: Object.keys(state.regionDetailsData || {}).length
    });

    const handleGenerateReport = async () => {
        const validation = validateForm();

        if (!validation.isValid) {
            setValidationErrors(validation.errors);
            setValidationDialog(true);
            return;
        }

        setSuccess(false);
        const result = await submitForm('/report/generate');

        if (result.success) {
            setSuccess(true);
            console.log('Report generated successfully');
        } else {
            console.error('Failed to generate report:', result.error);
        }
    };

    const handleCloseValidationDialog = () => {
        setValidationDialog(false);
        setValidationErrors([]);
    };

    // Calculate completion status
    const getCompletionStatus = () => {
        // Find existing non-empty region data from regionFormStates directly
        const activeRegionsData = Object.entries(state.regionFormStates || {})
            .filter(([_, regionState]) => {
                const formData = regionState.formData || {};
                return Object.keys(formData).length > 0;
            });

        const status = {
            patientData: !!state.patientData,
            canalsTraced: !!state.canalsTraced,
            panoramicImages: !!(state.panoramicViewData?.panoramicFile1 || state.panoramicViewData?.panoramicFile2),
            panoramicComments: !!state.panoramicViewData?.comments,
            dimensionsImages: !!(state.dimensionsData?.croppedImage1 || state.dimensionsData?.croppedImage2),
            regionDetails: activeRegionsData.length > 0
        };

        const completed = Object.values(status).filter(Boolean).length;
        const total = Object.keys(status).length;

        return { ...status, completed, total, percentage: Math.round((completed / total) * 100) };
    };

    const completionStatus = getCompletionStatus();

    // Get detailed data summary
    const getDataSummary = () => {
        const activeRegionsData = Object.values(state.regionFormStates || {})
            .filter(regionState => {
                const formData = regionState.formData || {};
                return Object.keys(formData).length > 0;
            });

        return {
            patient: {
                name: state.patientData?.PatientName || 'Not available',
                age: state.patientData?.Age || 'Not available',
                gender: state.patientData?.Gender || 'Not available',
                studyDate: state.patientData?.Date || 'Not available'
            },
            report: {
                type: state.reportType || 'Not selected',
                canalsCount: state.canalsTraced ? state.canalsTraced.split('\n').filter(c => c.trim()).length : 0,
                panoramicFiles: [state.panoramicViewData?.panoramicFile1, state.panoramicViewData?.panoramicFile2].filter(Boolean).length,
                dimensionFiles: [state.dimensionsData?.croppedImage1, state.dimensionsData?.croppedImage2].filter(Boolean).length,
                regionsCount: activeRegionsData.length,
                regionsWithImages: activeRegionsData.filter(regionState =>
                    regionState.croppedImageFile1 || regionState.croppedImageFile2
                ).length
            }
        };
    };

    const dataSummary = getDataSummary();
    const isMandible = state.reportType.includes('Mandible');
    const isMaxilla = state.reportType.includes('Maxilla');

    if (!state.patientData) {
        return (
            <Box p={3}>
                <Typography variant="h6" color="textSecondary">
                    Please load patient data first from the sidebar.
                </Typography>
            </Box>
        );
    }

    return (
        <Box p={3}>
            <Typography variant="h6" gutterBottom>
                Generate Report
            </Typography>

            {/* Completion Status */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Report Completion Status
                    </Typography>
                    <Box sx={{ mb: 2 }}>
                        <Typography variant="body2" color="textSecondary">
                            {completionStatus.completed} of {completionStatus.total} sections completed ({completionStatus.percentage}%)
                        </Typography>
                    </Box>

                    <Grid container spacing={2}>
                        <Grid item xs={12} sm={6} md={4}>
                            <Box display="flex" alignItems="center" mb={1}>
                                {completionStatus.patientData ?
                                    <CheckCircleIcon color="success" sx={{ mr: 1 }} /> :
                                    <ErrorIcon color="error" sx={{ mr: 1 }} />
                                }
                                <Typography variant="body2">Patient Data</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Box display="flex" alignItems="center" mb={1}>
                                {completionStatus.canalsTraced ?
                                    <CheckCircleIcon color="success" sx={{ mr: 1 }} /> :
                                    <WarningIcon color="warning" sx={{ mr: 1 }} />
                                }
                                <Typography variant="body2">Canals Traced</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Box display="flex" alignItems="center" mb={1}>
                                {completionStatus.panoramicImages ?
                                    <CheckCircleIcon color="success" sx={{ mr: 1 }} /> :
                                    <WarningIcon color="warning" sx={{ mr: 1 }} />
                                }
                                <Typography variant="body2">Panoramic Images</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Box display="flex" alignItems="center" mb={1}>
                                {completionStatus.panoramicComments ?
                                    <CheckCircleIcon color="success" sx={{ mr: 1 }} /> :
                                    <WarningIcon color="warning" sx={{ mr: 1 }} />
                                }
                                <Typography variant="body2">Panoramic Comments</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Box display="flex" alignItems="center" mb={1}>
                                {completionStatus.dimensionsImages ?
                                    <CheckCircleIcon color="success" sx={{ mr: 1 }} /> :
                                    <WarningIcon color="warning" sx={{ mr: 1 }} />
                                }
                                <Typography variant="body2">Dimensions Images</Typography>
                            </Box>
                        </Grid>

                        <Grid item xs={12} sm={6} md={4}>
                            <Box display="flex" alignItems="center" mb={1}>
                                {completionStatus.regionDetails ?
                                    <CheckCircleIcon color="success" sx={{ mr: 1 }} /> :
                                    <WarningIcon color="warning" sx={{ mr: 1 }} />
                                }
                                <Typography variant="body2">Region Details</Typography>
                            </Box>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Data Summary */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Report Data Summary
                    </Typography>

                    <Grid container spacing={3}>
                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" gutterBottom>
                                Patient Information
                            </Typography>
                            <Typography variant="body2"><strong>Name:</strong> {dataSummary.patient.name}</Typography>
                            <Typography variant="body2"><strong>Age:</strong> {dataSummary.patient.age}</Typography>
                            <Typography variant="body2"><strong>Gender:</strong> {dataSummary.patient.gender}</Typography>
                            <Typography variant="body2"><strong>Study Date:</strong> {dataSummary.patient.studyDate}</Typography>
                        </Grid>

                        <Grid item xs={12} md={6}>
                            <Typography variant="subtitle1" gutterBottom>
                                Report Content
                            </Typography>
                            <Typography variant="body2"><strong>Report Type:</strong> {dataSummary.report.type}</Typography>
                            <Typography variant="body2"><strong>Canals Traced:</strong> {dataSummary.report.canalsCount} descriptions</Typography>
                            <Typography variant="body2"><strong>Panoramic Files:</strong> {dataSummary.report.panoramicFiles} images</Typography>
                            <Typography variant="body2"><strong>Dimension Files:</strong> {dataSummary.report.dimensionFiles} images</Typography>
                            <Typography variant="body2"><strong>Region Details:</strong> {dataSummary.report.regionsCount} regions</Typography>
                            <Typography variant="body2"><strong>Regions with Images:</strong> {dataSummary.report.regionsWithImages} regions</Typography>
                        </Grid>
                    </Grid>
                </CardContent>
            </Card>

            {/* Sagittal and Sinus Views */}
            {isMandible && <SagittalViewTab />}
            {isMaxilla && (
                <>
                    <SinusView />
                    <SagittalViewTab />
                </>
            )}

            {/* Radiological Impressions */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Typography variant="h6" gutterBottom>
                        Radiological Impressions
                    </Typography>
                    <FormControl fullWidth>
                        <InputLabel id="radiological-impressions-label">Select Impressions</InputLabel>
                        <Select
                            labelId="radiological-impressions-label"
                            multiple
                            value={state.radiologicalImpressions || []}
                            onChange={handleImpressionsChange}
                            renderValue={(selected) => (
                                <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 0.5 }}>
                                    {selected.map((value) => (
                                        <Chip key={value} label={value} size="small" />
                                    ))}
                                </Box>
                            )}
                        >
                            {(state.reportType.includes('Mandible') ? impressions.mandible : impressions.maxilla).map((impression) => (
                                <MenuItem key={impression} value={impression}>
                                    <Checkbox checked={(state.radiologicalImpressions || []).indexOf(impression) > -1} />
                                    <ListItemText primary={impression} />
                                </MenuItem>
                            ))}
                        </Select>
                    </FormControl>
                    {(state.radiologicalImpressions || []).includes('Other') && (
                        <TextField
                            label="Other Impression"
                            fullWidth
                            margin="normal"
                            value={state.otherImpression || ''}
                            onChange={handleOtherImpressionChange}
                        />
                    )}
                </CardContent>
            </Card>

            {/* Region Details Summary */}
            {Object.keys(state.regionDetailsData || {}).length > 0 && (
                <Card variant="outlined" sx={{ mb: 3 }}>
                    <CardContent>
                        <Typography variant="h6" gutterBottom>
                            Selected Regions Summary
                        </Typography>
                        <Box sx={{ display: 'flex', flexWrap: 'wrap', gap: 1 }}>
                            {Object.entries(state.regionDetailsData || {}).map(([regionNumber, regionData]) => (
                                <Chip
                                    key={regionNumber}
                                    label={`Region ${regionNumber}`}
                                    color={regionData.croppedImageFile1 || regionData.croppedImageFile2 ? 'success' : 'default'}
                                    variant={regionData.croppedImageFile1 || regionData.croppedImageFile2 ? 'filled' : 'outlined'}
                                    size="small"
                                />
                            ))}
                        </Box>
                    </CardContent>
                </Card>
            )}

            {/* Generate Button */}
            <Card variant="outlined" sx={{ mb: 3 }}>
                <CardContent>
                    <Button
                        variant="contained"
                        color="primary"
                        onClick={handleGenerateReport}
                        disabled={state.loading || !computed.isFormValid()}
                        fullWidth
                        size="large"
                        startIcon={state.loading ? null : <DownloadIcon />}
                        sx={{ mb: 2, py: 2, maxWidth: '400px', display: 'block', mx: 'auto' }}
                    >
                        {state.loading ? 'Generating Report...' : 'Generate & Download Report'}
                    </Button>

                    {!computed.isFormValid() && (
                        <Alert severity="warning" sx={{ mt: 2 }}>
                            Please complete the required fields: Patient data, Unit selection, and Doctor selection.
                        </Alert>
                    )}
                </CardContent>
            </Card>

            {/* Success Message */}
            {success && (
                <Alert severity="success" sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Report Generated Successfully! 🎉
                    </Typography>
                    <Typography variant="body2">
                        Your CBCT report has been generated and should download automatically.
                        If the download doesn't start, please check your browser's download settings.
                    </Typography>
                </Alert>
            )}

            {/* Error Alert */}
            {state.error && (
                <Alert severity="error" sx={{ mt: 2 }}>
                    <Typography variant="h6" gutterBottom>
                        Report Generation Failed
                    </Typography>
                    <Typography variant="body2">
                        {state.error}
                    </Typography>
                </Alert>
            )}

            {/* Validation Dialog */}
            <Dialog
                open={validationDialog}
                onClose={handleCloseValidationDialog}
                maxWidth="sm"
                fullWidth
            >
                <DialogTitle>
                    <Box display="flex" alignItems="center">
                        <ErrorIcon color="error" sx={{ mr: 1 }} />
                        Validation Errors
                    </Box>
                </DialogTitle>
                <DialogContent>
                    <Typography variant="body2" sx={{ mb: 2 }}>
                        Please fix the following issues before generating the report:
                    </Typography>
                    <List dense>
                        {validationErrors.map((error, index) => (
                            <ListItem key={index}>
                                <ListItemIcon>
                                    <ErrorIcon color="error" fontSize="small" />
                                </ListItemIcon>
                                <ListItemText primary={error} />
                            </ListItem>
                        ))}
                    </List>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseValidationDialog} color="primary">
                        Close
                    </Button>
                </DialogActions>
            </Dialog>

            {/* Help Text */}
            <Box sx={{ mt: 3, p: 2, bgcolor: 'grey.50', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                    💡 Tips for Report Generation:
                </Typography>
                <Typography variant="body2" component="ul" sx={{ pl: 2, mb: 0 }}>
                    <li>Ensure all required sections are completed (marked with ✓)</li>
                    <li>Upload images for better report quality</li>
                    <li>Add detailed comments in each section</li>
                    <li>Review all measurements before generating</li>
                    <li>The report will be downloaded as a .docx file</li>
                </Typography>
            </Box>
        </Box>
    );
};

export default GenerateReportTab;